import { useEffect, useState } from 'react'
import {
  Button,
  NativeSelect,
  NumberInput,
  Radio,
  SegmentedControl,
  Text,
  TextInput,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { showNotification } from '@mantine/notifications'
import { IconAlertCircle } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import {
  EOB_RESPONSIBILITY_KINDS,
  type PatientInvoiceKind,
  type PaymentMethod,
} from '@/model/middleware-types'
import { PATIENT_INVOICE_KIND_LABELS } from '@/model/patient-invoice-kind'
import { formatCents, todayBillingDate } from '../utils'
import type { BillingCaseDetail } from './CaseDrawer'

type PatientInvoice = BillingCaseDetail['patientInvoices'][number]

export type PatientPaymentMode = 'charge' | 'link' | 'manual'

const METHOD_LABELS: Record<PaymentMethod, string> = {
  card: 'Card',
  vcc: 'Virtual card',
  ach_eft: 'ACH / EFT',
  check: 'Check',
}

type AddPatientPaymentFormProps = {
  encounterId: string
  invoices: PatientInvoice[]
  /** Case patient responsibility; null when the EOB has not been entered. */
  patientRespCents: number | null
  selfPay: boolean
  initialKind: PatientInvoiceKind
  initialMode?: PatientPaymentMode
  /** Tells the drawer whether the form holds unsaved input (it asks before closing). */
  onDirtyChange?: (dirty: boolean) => void
  /** Case detail refreshed + back to the read view. */
  onSuccess: () => void
  onCancel: () => void
}

// The drawer's write side for Co-pay / Deductible / Co-insurance, read top-down: what the
// payment is for, how much, how to collect it (charge card / send link / record manually,
// each with a one-line hint). A paid (or refunded) kind is locked — one Invoice per kind.
// The amount prefills from the kind's own Invoice (draft or link sent), else from what is
// left of the patient responsibility.
export function AddPatientPaymentForm({
  encounterId,
  invoices,
  patientRespCents,
  selfPay,
  initialKind,
  initialMode = 'charge',
  onDirtyChange,
  onSuccess,
  onCancel,
}: AddPatientPaymentFormProps) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  const kinds: readonly PatientInvoiceKind[] = selfPay ? ['self-pay'] : EOB_RESPONSIBILITY_KINDS

  const prefillCents = (forKind: PatientInvoiceKind): number | null => {
    const own = invoices.find(
      (invoice) =>
        invoice.kind === forKind && (invoice.status === 'draft' || invoice.status === 'issued'),
    )
    if (forKind === 'self-pay') return own?.amountCents ?? null
    if (own) return own.amountCents
    if (patientRespCents === null) return null

    const othersCents = invoices
      .filter((invoice) => invoice.kind !== forKind)
      .reduce((sum, invoice) => sum + invoice.amountCents, 0)
    return Math.max(0, patientRespCents - othersCents)
  }

  const [kind, setKind] = useState<PatientInvoiceKind>(initialKind)
  const [amountCents, setAmountCents] = useState<number | null>(() => prefillCents(initialKind))
  const [mode, setMode] = useState<PatientPaymentMode>(initialMode)
  const [method, setMethod] = useState<PaymentMethod>('card')
  const [paymentDate, setPaymentDate] = useState<string | null>(todayBillingDate())
  const [reference, setReference] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Input away from its starting value — the drawer asks before closing over it. Picking
  // another kind or mode alone is not input.
  const isDirty =
    amountCents !== prefillCents(kind) ||
    method !== 'card' ||
    paymentDate !== todayBillingDate() ||
    reference.trim() !== ''

  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  const createCheckout = useMutation(orpc.createCopayCheckout.mutationOptions())
  const sendLink = useMutation(orpc.sendCopayLink.mutationOptions())
  const recordPatientPayment = useMutation(orpc.recordPatientPayment.mutationOptions())

  const lockedInvoices = invoices.filter(
    (invoice) => invoice.status === 'balanced' || invoice.status === 'cancelled',
  )

  const label = PATIENT_INVOICE_KIND_LABELS[kind]
  const ownInvoice = invoices.find(
    (invoice) =>
      invoice.kind === kind && (invoice.status === 'draft' || invoice.status === 'issued'),
  )
  const amountHint = ownInvoice
    ? ownInvoice.status === 'draft'
      ? 'From the draft'
      : 'From the link already sent'
    : patientRespCents !== null
      ? 'What is left of the patient responsibility'
      : undefined
  const cents = amountCents ?? 0
  const canSubmit = mode === 'manual' ? cents >= 1 && paymentDate !== null : cents >= 50
  const isSubmitting =
    createCheckout.isPending || sendLink.isPending || recordPatientPayment.isPending

  const changeKind = (value: PatientInvoiceKind) => {
    setKind(value)
    setAmountCents(prefillCents(value))
    setError(null)
  }

  const submit = async () => {
    setError(null)

    try {
      if (mode === 'manual') {
        await recordPatientPayment.mutateAsync({
          encounterId,
          kind,
          amountCents: cents,
          method,
          paymentDate: paymentDate ?? '',
          reference: reference.trim() || undefined,
        })
        showNotification({ color: 'green', message: `${label} ${formatCents(cents)} recorded` })
      } else {
        const session = await createCheckout.mutateAsync({ encounterId, kind, amountCents: cents })

        if (mode === 'charge') {
          window.open(session.checkoutUrl, '_blank', 'noopener')
          showNotification({ color: 'green', message: `Checkout opened for ${formatCents(cents)}` })
        } else {
          const sent = await sendLink.mutateAsync({ encounterId, kind, channel: 'email' })
          showNotification({ color: 'green', message: `Payment link emailed to ${sent.sentTo}` })
        }
      }

      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.getCopayStatus.key() })
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the patient payment')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Text size="md" fw={700} c="dark">
        {ownInvoice ? 'Collect' : 'Add'} {label.toLowerCase()}
      </Text>

      <div className="flex flex-col gap-1">
        <Text size="sm" fw={500} c="dark">
          Payment for
        </Text>
        <SegmentedControl
          fullWidth
          value={kind}
          onChange={(value) => changeKind(value as PatientInvoiceKind)}
          data={kinds.map((forKind) => {
            const locked = lockedInvoices.find((invoice) => invoice.kind === forKind)
            const suffix = locked ? (locked.status === 'cancelled' ? ' · Refunded' : ' · Paid') : ''
            return {
              value: forKind,
              label: `${PATIENT_INVOICE_KIND_LABELS[forKind]}${suffix}`,
              disabled: Boolean(locked),
            }
          })}
        />
      </div>

      <NumberInput
        label="Amount"
        description={amountHint}
        prefix="$"
        min={0}
        decimalScale={2}
        value={amountCents === null ? '' : amountCents / 100}
        onChange={(v) => setAmountCents(typeof v === 'number' ? Math.round(v * 100) : null)}
      />

      <Radio.Group
        label="How to collect"
        value={mode}
        onChange={(value) => setMode(value as PatientPaymentMode)}
      >
        <div className="mt-1 flex flex-col gap-2">
          <Radio
            value="charge"
            label="Charge card"
            description="Open a Stripe checkout and take the patient's card now"
          />
          <Radio value="link" label="Send link" description="Email the patient a payment link" />
          <Radio
            value="manual"
            label="Record manually"
            description="Cash, check or card already received"
          />
        </div>
      </Radio.Group>

      {mode === 'manual' ? (
        <div className="grid grid-cols-2 gap-3">
          <NativeSelect
            label="Method"
            value={method}
            onChange={(e) => setMethod(e.currentTarget.value as PaymentMethod)}
            data={Object.entries(METHOD_LABELS).map(([value, methodLabel]) => ({
              value,
              label: methodLabel,
            }))}
          />
          <DateInput
            label="Payment date"
            valueFormat="MM/DD/YYYY"
            value={paymentDate}
            onChange={setPaymentDate}
          />
          <TextInput
            label="Reference"
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
          />
        </div>
      ) : null}

      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <IconAlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <Text size="sm" c="red.8">
            {error}
          </Text>
        </div>
      ) : null}

      <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} loading={isSubmitting} onClick={submit}>
          {mode === 'charge'
            ? `Charge ${formatCents(cents)}`
            : mode === 'link'
              ? 'Send link'
              : `Record ${formatCents(cents)}`}
        </Button>
      </div>
    </div>
  )
}
